/**
 * Sprint 22 — OAuth-required prompt card (ported from Sprint 7.x). Surfaces when
 * a connected tool needs the user to authorize; opens the auth flow in a popup
 * and listens for the postMessage completion.
 */
import { useState, useEffect, useRef } from "react";
import { ExternalLink, Check, AlertTriangle, Loader2 } from "@icons";
import { TOOLS_CHANGED_EVENT } from "../../hooks/use-tools-changed";

interface OAuthPromptProps {
  connectionId: string;
  connectionName: string;
  authUrl: string;
  /**
   * Sprint 24.12 — re-run the user's last turn once the connection is live, so
   * they don't have to retype. ChatPanel wires this to the runtime's regenerate().
   */
  onContinue?: () => void;
}

export function OAuthPromptCard({ connectionId, connectionName, authUrl, onContinue }: OAuthPromptProps) {
  const [status, setStatus] = useState<"pending" | "authorizing" | "connected" | "error">("pending");
  const popupRef = useRef<Window | null>(null);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === "oauth_complete" && event.data?.connectionId === connectionId) {
        setStatus("connected");
        popupRef.current?.close();
        // Sprint 24.12 — a connector authorize changes the available-tool surface,
        // but it isn't a chat tool call, so per-tool invalidation never sees it.
        // Broadcast so use-tools-changed refreshes the catalogue/equipment caches.
        window.dispatchEvent(
          new CustomEvent(TOOLS_CHANGED_EVENT, { detail: { source: "oauth", connectionId } }),
        );
      }
      if (event.data?.type === "oauth_error" && event.data?.connectionId === connectionId) {
        setStatus("error");
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [connectionId]);

  const handleAuthorize = () => {
    setStatus("authorizing");
    popupRef.current = window.open(
      authUrl,
      `oauth_${connectionId}`,
      "width=600,height=700,popup=yes",
    );

    const interval = setInterval(() => {
      if (popupRef.current?.closed) {
        clearInterval(interval);
        setStatus((s) => (s === "authorizing" ? "pending" : s));
      }
    }, 1000);
  };

  if (status === "connected") {
    return (
      <div className="my-2 rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-3">
        <div className="flex items-center gap-2">
          <Check size={16} className="text-emerald-400" />
          <span className="text-sm font-medium text-emerald-400">
            Connected to {connectionName}
          </span>
        </div>
        <p className="mt-1 text-xs text-on-surface-variant/50">
          Your account is now linked.
        </p>
        {onContinue && (
          <button
            type="button"
            onClick={onContinue}
            className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-emerald-500/15 px-3 py-1.5 text-xs font-medium text-emerald-400 hover:bg-emerald-500/25 transition-colors"
          >
            <i className="ti ti-arrow-right" style={{ fontSize: 12 }} aria-hidden="true" />
            Continue
          </button>
        )}
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="my-2 rounded-xl border border-error/20 bg-error/5 px-4 py-3">
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} className="text-error" />
          <span className="text-sm font-medium text-error">
            Authorization failed for {connectionName}
          </span>
        </div>
        <p className="mt-1 text-xs text-on-surface-variant/50">
          Something went wrong. Please try again.
        </p>
        <button
          type="button"
          onClick={handleAuthorize}
          className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-xs font-medium text-primary hover:bg-white/15 transition-colors"
        >
          <ExternalLink size={12} />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="my-2 rounded-xl border border-secondary/20 bg-secondary/5 px-4 py-3">
      <div className="flex items-center gap-2">
        <ExternalLink size={16} className="text-secondary" />
        <span className="text-sm font-medium text-primary">
          Connect to {connectionName}
        </span>
      </div>
      <p className="mt-1 text-xs text-on-surface-variant/50">
        This agent needs access to your {connectionName} account to use its tools.
        Click below to authorize.
      </p>
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={handleAuthorize}
          disabled={status === "authorizing"}
          className="inline-flex items-center gap-1.5 rounded-lg bg-secondary px-3 py-1.5 text-xs font-medium text-white hover:bg-secondary/90 transition-colors disabled:opacity-60"
        >
          {status === "authorizing" ? (
            <>
              <Loader2 size={12} className="animate-spin" />
              Authorizing…
            </>
          ) : (
            <>
              <ExternalLink size={12} />
              Authorize {connectionName}
            </>
          )}
        </button>
      </div>
    </div>
  );
}
